import { PageHeader, Panel, Toast } from '@deque/cauldron-react';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Link, Route, Switch, useLocation } from 'react-router-dom';
import PageTitle from '~/modules/common/components/PageTitle';
import { useAuthContext } from '~/modules/common/contexts/auth';
import useMediaQuery from '~/modules/common/hooks/useMediaQuery';
import AdminFeatureFlagsContainer from '../../containers/AdminFeatureFlags';
import EnterpriseFeatureFlagOverridesContainer from '../../containers/EnterpriseFeatureFlagOverrides';
import useAdminFeatureFlags from '../../hooks/useAdminFeatureFlags';
import useSSOConfig from '../../hooks/useSSOConfig';
import styles from './FeaturesAdmin.css';

const ADMIN_GROUP = window.appConfig.FEATURE_FLAG_ADMIN_GROUP;

const PATHS = {
  GLOBAL_OVERRIDES: '/internal/features/admin',
  ENTERPRISES_OVERRIDES: '/internal/features/admin/enterprises'
} as const;

if (!ADMIN_GROUP) {
  throw new Error('Missing FEATURE_FLAG_ADMIN_GROUP variable');
}

const FeaturesAdmin: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const location = useLocation();
  const narrow = useMediaQuery('(max-width: 91rem)');

  const {
    features,
    loading: featuresLoading,
    error: featuresError,
    refresh: refreshFeatures
  } = useAdminFeatureFlags(user?.token ?? '');
  const {
    data: ssoConfig,
    error: ssoConfigError,
    loading: ssoConfigLoading
  } = useSSOConfig();

  const pathsState = useMemo(() => {
    const { pathname } = location;
    return {
      globalIsActive: pathname === PATHS.GLOBAL_OVERRIDES,
      enterprisesIsActive: pathname === PATHS.ENTERPRISES_OVERRIDES
    };
  }, [location]);

  const isAdmin = user?.groups.includes(ADMIN_GROUP) ?? false;

  if (user === undefined || !isAdmin) {
    return (
      <div className={classNames(styles.page, styles.permissionDenied)}>
        <PageTitle title={t('Feature Flag Admin Panel')} />
        <PageHeader heading={<h1>{t('Feature Flag Admin Panel')}</h1>} />
        <h2>{t('You do not have permission to see this page.')}</h2>
      </div>
    );
  }

  const containerProps = {
    narrow,
    user,
    features,
    featuresError,
    featuresLoading,
    ssoConfig,
    ssoConfigLoading,
    onRefreshFeatures: refreshFeatures
  };

  return (
    <div className={styles.page}>
      <Toast type="error" show={!!ssoConfigError}>
        {t('Failed to load SSO config: {{error}}', {
          error: ssoConfigError?.message
        })}
      </Toast>
      <PageTitle title={t('Feature Flag Admin Panel')} />
      <PageHeader
        className={styles.pageHeader}
        heading={<h1>{t('Feature Flag Admin Panel')}</h1>}
      />
      <Panel>
        <nav
          aria-label={t('Feature flag override type')}
          className={styles.tabs}
        >
          <ul>
            <li>
              <Link
                to={PATHS.GLOBAL_OVERRIDES}
                aria-current={pathsState.globalIsActive}
              >
                {t('Global Overrides')}
              </Link>
            </li>
            <li>
              <Link
                to={PATHS.ENTERPRISES_OVERRIDES}
                aria-current={pathsState.enterprisesIsActive}
              >
                {t('Enterprise Overrides')}
              </Link>
            </li>
          </ul>
        </nav>
        <Switch>
          <Route exact path={PATHS.GLOBAL_OVERRIDES}>
            <AdminFeatureFlagsContainer {...containerProps} />
          </Route>
          <Route path={PATHS.ENTERPRISES_OVERRIDES}>
            <EnterpriseFeatureFlagOverridesContainer {...containerProps} />
          </Route>
        </Switch>
      </Panel>
    </div>
  );
};

export default FeaturesAdmin;
